import { useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { TableCellType } from '../Table'
import './TableCell.scss'

export const TableCell = <T extends { [key: string]: any }>({
  row,
  column,
  colWidth,
}: TableCellType<T>) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const [rect, setRect] = useState<DOMRect>()

  const measuredWrapperRef = useCallback(
    (node: HTMLTableCellElement) => {
      if (node !== null) {
        setRect(node.getBoundingClientRect())
      }
    },
    [showTooltip],
  )

  const _convertObjArrayValue = (value: any) => {
    if (Array.isArray(value))
      // Filters out objects from array
      return value.filter((item) => typeof item !== 'object').join(', ')
    if (typeof value === 'object' && value !== null) {
      console.warn('TableRow: Column path should not return an object.')
      return ''
    }
    return value
  }

  const _handleRowData = (path: string | string[]) => {
    if (Array.isArray(path)) {
      let current = row
      for (let i = 0; i < path.length; i += 1) {
        if (current[path[i]] === undefined) {
          return undefined
        }
        current = current[path[i]]
      }
      return _convertObjArrayValue(current)
    }
    return _convertObjArrayValue(row[path])
  }

  const getFlexAlign = (alignment: 'left' | 'right' | 'center' | undefined) => {
    if (alignment === 'center') return 'justify-center'
    if (alignment === 'right') return 'justify-end'
    return 'justify-start'
  }

  const key = column.path
  if (column.RowCell) {
    return (
      <td
        className={`TableCell${
          column.alignment ? ` TableCell--${column.alignment}` : ''
        }`}
      >
        <div
          className={`TableCell__flex-container ${getFlexAlign(
            column.alignment,
          )}`}
        >
          {column.RowCell(row)}
        </div>
      </td>
    )
  }
  if (key) {
    return (
      <>
        <td
          className={`TableCell${
            column.alignment ? `TableCell--${column.alignment}` : ''
          } break-all leading-none text-s`}
          ref={measuredWrapperRef}
          onMouseEnter={(e) => {
            const cell = e.target as HTMLTableCellElement
            if (cell.scrollWidth > cell.offsetWidth) {
              setShowTooltip(true)
            }
          }}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <span className="TableCell__inner-container">
            {_handleRowData(key)}
            {showTooltip &&
              createPortal(
                <div
                  className="TableCell__text-overflow"
                  style={{
                    top: rect ? rect.top - 1 : undefined,
                    left: rect ? rect.left - 1 : undefined,
                    height: rect ? rect.height : undefined,
                  }}
                >
                  {_handleRowData(key)}
                </div>,
                document.body,
              )}
          </span>
        </td>
      </>
    )
  }
  return <td />
}
